import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const fmtINR = (n) => {
  if (n == null) return '₹0';
  return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

function SubscriptionDashboard({ token }) {
  const { isMobile } = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const headers = () => ({
    Authorization: `Bearer ${token}`
  });

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/subscription/admin/dashboard', { headers: headers() });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) throw new Error('Server unavailable — please try again');
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to load dashboard');
      setData(json);
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const statusCards = [
    { key: 'active', label: 'Active Subscriptions', color: '#16a34a', bg: '#f0fdf4' },
    { key: 'trial', label: 'Trial', color: '#2563eb', bg: '#eff6ff' },
    { key: 'past_due', label: 'Past Due', color: '#ea580c', bg: '#fff7ed' },
    { key: 'grace', label: 'Grace', color: '#dc2626', bg: '#fef2f2' },
    { key: 'expired', label: 'Expired', color: '#6b7280', bg: '#f9fafb' },
    { key: 'cancelled', label: 'Cancelled', color: '#6b7280', bg: '#f9fafb' }
  ];

  const revenueCards = [
    { key: 'mrr', label: 'Monthly Recurring Revenue (MRR)' },
    { key: 'arr', label: 'Annual Recurring Revenue (ARR)' },
    { key: 'revenue_last_30', label: 'Revenue Last 30 Days' }
  ];

  return (
    <div style={{ padding: isMobile ? 12 : 24, maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 20px', color: 'var(--theme-text, #1f2937)', fontSize: isMobile ? 18 : 22 }}>
        Subscription Dashboard
      </h2>

      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, background: '#fef2f2',
          border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13
        }}>
          {error}
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</div>}

      {!loading && data && (
        <>
          {/* Status Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: isMobile ? 10 : 16,
            marginBottom: 24
          }}>
            {statusCards.map((c) => (
              <div key={c.key} style={{
                background: c.bg,
                border: `1px solid var(--theme-border, #e5e7eb)`,
                borderRadius: 8,
                padding: isMobile ? '14px 12px' : '18px 20px',
                borderLeft: `4px solid ${c.color}`
              }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {c.label}
                </div>
                <div style={{ fontSize: isMobile ? 24 : 32, fontWeight: 700, color: c.color }}>
                  {data[c.key] ?? 0}
                </div>
              </div>
            ))}
          </div>

          {/* Revenue Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? 10 : 16
          }}>
            {revenueCards.map((c) => (
              <div key={c.key} style={{
                background: 'var(--theme-surface, #fff)',
                border: '1px solid var(--theme-border, #e5e7eb)',
                borderRadius: 8,
                padding: isMobile ? '14px 12px' : '20px 24px'
              }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {c.label}
                </div>
                <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, color: 'var(--theme-primary, #2563eb)' }}>
                  {fmtINR(data[c.key])}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default SubscriptionDashboard;
